/**
 * LoginScreen.jsx — Best-in-class auth (v2)
 *
 * What's new vs v1:
 *  Social-first layout  — Google & LinkedIn above the email form (lower-friction happy path)
 *  Progressive disclosure — "or continue with email" toggle on login; always open on signup
 *  Animated transitions  — fade+slide between Login / Signup / Reset (modeKey re-mounts)
 *  Shake on error        — form shakes + haptic vibration on failure
 *  Mount animation       — slide-up on mobile
 *  Inline per-field errors — validated on blur, not just on submit
 *  Auto-focus            — first visible field receives focus after each mode change
 *  Contextual error CTAs — email-conflict error offers "Try Google" button
 *  Forgot-password inline — label-row shortcut (no separate tab needed)
 *  All v1 bug fixes retained (email trim, setLoading finally, confirm-pw, terms, etc.)
 */

import React from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { EyeIcon, EyeOffIcon, AlertTriangle, CheckIcon, LoaderIcon } from "../ui/Icons";

var MO = { LOGIN: "login", REGISTER: "register", RESET: "reset" };

var AUTH_ERRORS = {
  "auth/invalid-credential":    "Incorrect email or password.",
  "auth/user-not-found":        "No account found with this email.",
  "auth/wrong-password":        "Incorrect password.",
  "auth/email-already-in-use":  "An account with this email already exists. Try logging in or use \"Forgot password\" to reset.",
  "auth/weak-password":         "Password must be at least 6 characters.",
  "auth/invalid-email":         "Please enter a valid email address.",
  "auth/too-many-requests":     "Too many attempts. Please try again later.",
  "auth/network-request-failed":"Network error. Check your connection.",
  "auth/user-disabled":         "This account has been disabled. Contact support.",
  "auth/popup-closed-by-user":  "Sign-in window was closed before completing.",
  "auth/popup-blocked":         "Browser blocked the popup. Allow popups and try again.",
  "auth/operation-not-allowed": "Google sign-in is not enabled.",
  "auth/unauthorized-domain":   "This domain is not authorised for Google sign-in.",
  "auth/account-exists-with-different-credential":
    "An account already exists with this email under a different sign-in method.",
};
var friendlyError   = (code) => AUTH_ERRORS[code] ?? "Something went wrong. Please try again.";
var isEmailConflict = (code) =>
  code === "auth/email-already-in-use" ||
  code === "auth/account-exists-with-different-credential";

var T = {
  bg:            "#0a0f1e",
  text:          "#f1f5f9",
  muted:         "#94a3b8",
  dim:           "#64748b",
  purple:        "#a5b4fc",
  border:        "rgba(255,255,255,0.08)",
  inpBg:         "rgba(255,255,255,0.03)",
  focusBorder:   "rgba(99,102,241,0.55)",
  focusShadow:   "0 0 0 3px rgba(99,102,241,0.14)",
  errorColor:    "#ef4444",
  errorBg:       "rgba(239,68,68,0.08)",
  errorBorder:   "rgba(239,68,68,0.32)",
  successBg:     "rgba(34,197,94,0.08)",
  successBorder: "rgba(34,197,94,0.32)",
};
var FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

var GCSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700&display=swap');
.auth-page *,.auth-page *::before,.auth-page *::after{box-sizing:border-box}
.auth-page input[type="checkbox"]{accent-color:#6366f1}
.auth-input:hover:not(:focus){border-color:rgba(255,255,255,0.16)!important}
.auth-tab-btn{transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1)}
.auth-tab-btn:hover{color:#cbd5e1!important}
.auth-link:hover{color:#c7d2fe!important;text-decoration:underline}
.auth-eye:hover{color:#94a3b8!important;background:rgba(255,255,255,0.04)!important}
.auth-submit:hover:not(:disabled){transform:translateY(-1.5px);box-shadow:0 12px 32px rgba(79,70,229,0.5)!important}
.auth-submit:active:not(:disabled){transform:translateY(0)}
.auth-google:hover:not(:disabled){background:#f8fafc!important;box-shadow:0 12px 24px rgba(0,0,0,0.25)!important;transform:translateY(-1.5px)}
.auth-google:active:not(:disabled){transform:translateY(0)}
.auth-linkedin:hover:not(:disabled){background:#137ac2!important;box-shadow:0 12px 24px rgba(10,102,194,0.35)!important;transform:translateY(-1.5px)}
.auth-linkedin:active:not(:disabled){transform:translateY(0)}
.auth-cfta:hover{background:rgba(255,255,255,0.10)!important}
@keyframes authShake{0%,100%{transform:translateX(0)}15%{transform:translateX(-6px)}30%{transform:translateX(5px)}45%{transform:translateX(-4px)}60%{transform:translateX(3px)}75%{transform:translateX(-2px)}90%{transform:translateX(1px)}}
.auth-shake{animation:authShake 0.45s ease}
@keyframes feIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
.auth-fe{animation:feIn 0.18s ease}
@keyframes modeIn{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:translateY(0)}}
.auth-mode{animation:modeIn 0.22s ease}
@keyframes emailIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
.auth-email-in{animation:emailIn 0.2s ease}
@keyframes mUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
.auth-input:-webkit-autofill,
.auth-input:-webkit-autofill:hover,
.auth-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f5f9 !important;
  -webkit-box-shadow: 0 0 0px 1000px #0c1328 inset !important;
  transition: background-color 5000s ease-in-out 0s;
  border-color: rgba(255,255,255,0.08) !important;
}
.auth-mprops{display:none}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
@keyframes toastIn{from{opacity:0;transform:translateY(-10px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
.auth-toast{animation:toastIn 0.25s cubic-bezier(0.16,1,0.3,1)}
.auth-toast-retry:hover{background:rgba(99,102,241,0.25)!important}
@media(max-width:480px){.auth-toast{left:16px;right:16px;max-width:none!important}}
@media(max-width:900px){
  .auth-page{flex-direction:column!important}
  .auth-brand{display:none!important}
  .auth-panel{max-width:100%!important;width:100%!important;padding:32px 24px!important;animation:mUp 0.3s ease}
  .auth-mhdr{display:flex!important}
  .auth-mprops{display:block!important}
}
@media(max-width:480px){
  .auth-panel{padding:24px 18px!important}
  .auth-h2{font-size:22px!important}
}
`;

function calcStr(p) {
  if (!p) return { pct:0, label:"", color:"#1e293b" };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const i = Math.min(s, 4);
  return { pct:((i+1)/5)*100, label:["Very weak","Weak","Okay","Strong","Very strong"][i], color:["#ef4444","#f97316","#eab308","#22c55e","#10b981"][i] };
}

export default function LoginScreen({ initialMode = MO.LOGIN, ssoError = null, appSettings = {} }) {
  const [mode,   setMode]   = React.useState(initialMode === "register" ? MO.REGISTER : MO.LOGIN);
  const [email,  setEmail]  = React.useState("");
  const [pw,     setPw]     = React.useState("");
  const [cpw,    setCpw]    = React.useState("");
  const [name,   setName]   = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [showC,  setShowC]  = React.useState(false);
  const [err,    setErr]    = React.useState("");
  const [eCode,  setECode]  = React.useState("");
  const [ok,     setOk]     = React.useState("");
  const [busy,   setBusy]   = React.useState(false);
  const [focus,  setFocus]  = React.useState("");
  const [mKey,   setMKey]   = React.useState(0);
  const [touched,setTouched]= React.useState({});
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [linkedinLoading, setLinkedinLoading] = React.useState(false);
  const [showMobileProps, setShowMobileProps] = React.useState(false);
  const [toast,  setToast]  = React.useState(null);

  const formRef = React.useRef(null);
  const f1Ref   = React.useRef(null);

  const clr = () => { setErr(""); setECode(""); setOk(""); };
  const mk  = (f) => setTouched(t => ({ ...t, [f]:true }));

  React.useEffect(() => {
    const t = setTimeout(() => f1Ref.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [mode]);

  const seedG = React.useCallback(async (cred) => {
    const ref  = doc(db,"users",cred.user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) return null;
    if (appSettings.allowSignup === false) { await auth.signOut(); return "New signups are currently closed."; }
    const ex = appSettings.newUserBonusCredits > 0 ? appSettings.newUserBonusCredits : undefined;
    const un = cred.user.displayName || (cred.user.email||"").split("@")[0];
    const ue = (cred.user.email||"").toLowerCase();
    await setDoc(ref,{ name:un, email:ue, photoURL:cred.user.photoURL||null, role:"user",
      createdAt:serverTimestamp(), provider:"google", status:"active", ...(ex?{extraCredits:ex}:{}) });
    cred.user.getIdToken().then(tok => fetch("https://asia-south1-cv-editor-pro.cloudfunctions.net/sendWelcomeEmail",{
      method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${tok}`},
      body:JSON.stringify({name:un,email:ue}) }).catch(()=>{})).catch(()=>{});
    return null;
  // eslint-disable-next-line
  }, [appSettings.allowSignup, appSettings.newUserBonusCredits]);

  React.useEffect(() => {
    getRedirectResult(auth)
      .then(async c => { if (c) { const e = await seedG(c); if (e) setErr(e); }})
      .catch(e => setErr(friendlyError(e.code)));
  }, [seedG]); // eslint-disable-line

  // LinkedIn/SSO failures arrive as a plain message via the ssoError prop (redirect
  // round-trip has no error code) — surface as a dismissible toast with 1-click retry
  // instead of a mode-blocking banner.
  React.useEffect(() => {
    if (ssoError) setToast({ message: ssoError, retry: handleLI });
  }, []); // eslint-disable-line

  const shake = () => {
    if (navigator.vibrate) navigator.vibrate([8,50,8]);
    const el = formRef.current;
    if (!el) return;
    el.classList.remove("auth-shake");
    void el.offsetWidth;
    el.classList.add("auth-shake");
    el.addEventListener("animationend", () => el.classList.remove("auth-shake"), { once:true });
  };

  const sw = (m) => {
    setMode(m); setMKey(k=>k+1); clr();
    setPw(""); setCpw(""); setShowPw(false); setTouched({});
  };

  const mob = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleGoogle = async () => {
    clr(); setBusy(true); setGoogleLoading(true);
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ prompt:"select_account" });
    if (mob()) { await signInWithRedirect(auth,p).catch(e=>{setErr(friendlyError(e.code));setBusy(false);setGoogleLoading(false);}); return; }
    try {
      const c = await signInWithPopup(auth,p);
      const e = await seedG(c);
      if (e) { setErr(e); shake(); }
    } catch(e) {
      if (e.code==="auth/popup-closed-by-user"||e.code==="auth/cancelled-popup-request") {
        setToast({ message:"Sign-in window was closed before completing.", retry:handleGoogle });
      } else if (e.code==="auth/popup-blocked") {
        setToast({ message:"Browser blocked the popup. Allow popups for this site and try again.", retry:handleGoogle });
      } else {
        setErr(friendlyError(e.code)); setECode(e.code||""); shake();
      }
    } finally { setBusy(false); setGoogleLoading(false); }
  };

  const handleLI = () => { if (!busy) { setBusy(true); setLinkedinLoading(true); window.location.href = "/api/auth/linkedin"; } };

  const fe = React.useMemo(() => {
    const e={}, te=email.trim();
    if (touched.name&&mode===MO.REGISTER&&!name.trim())     e.name="Name is required.";
    if (touched.email&&!te)                                  e.email="Email is required.";
    else if(touched.email&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(te)) e.email="Enter a valid email address.";
    if (touched.pw&&mode!==MO.RESET&&!pw)                   e.pw="Password is required.";
    if (touched.pw&&mode===MO.REGISTER&&pw&&pw.length<8)    e.pw="At least 8 characters required.";
    if (touched.cpw&&mode===MO.REGISTER&&cpw&&pw!==cpw)     e.cpw="Passwords don't match.";
    return e;
  }, [touched,name,email,pw,cpw,mode]);

  const validate = () => {
    const te=email.trim();
    if (!te)                                         return "Please enter your email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(te))     return "Please enter a valid email address.";
    if (mode===MO.RESET) return null;
    if (!pw)                                         return "Please enter your password.";
    if (mode===MO.REGISTER) {
      if (!name.trim())                              return "Please enter your name.";
      if (pw.length<8)                               return "Password must be at least 8 characters.";
      if (pw!==cpw)                                  return "Passwords don't match.";
    }
    return null;
  };

  const handleSubmit = async () => {
    clr();
    setTouched({name:true,email:true,pw:true,cpw:true});
    const ve=validate();
    if (ve) { setErr(ve); shake(); return; }
    const te=email.trim().toLowerCase();
    setBusy(true);
    try {
      if (mode===MO.RESET) {
        await sendPasswordResetEmail(auth,te);
        setOk("Reset link sent — check your inbox.");
        return;
      }
      if (mode===MO.LOGIN) {
        await signInWithEmailAndPassword(auth,te,pw);
      } else {
        if (appSettings.allowSignup===false) { setErr("New signups are currently closed."); shake(); return; }
        const c = await createUserWithEmailAndPassword(auth,te,pw);
        const ex = appSettings.newUserBonusCredits>0 ? appSettings.newUserBonusCredits : undefined;
        await setDoc(doc(db,"users",c.user.uid),{
          name:name.trim(), email:te, role:"user", status:"active", createdAt:serverTimestamp(),
          ...(ex?{extraCredits:ex}:{})
        });
        c.user.getIdToken().then(tok => fetch("https://asia-south1-cv-editor-pro.cloudfunctions.net/sendWelcomeEmail",{
          method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${tok}`},
          body:JSON.stringify({name:name.trim(),email:te}) }).catch(()=>{})).catch(()=>{});
      }
    } catch(e) {
      setErr(friendlyError(e.code)); setECode(e.code||""); shake();
    } finally { setBusy(false); }
  };

  // preventDefault stops the native form submission Enter would otherwise
  // also trigger (via the form's onSubmit below) — without it, handleSubmit
  // ran twice on Enter (duplicate signup/reset-email network calls).
  const onKey = e => { if (e.key==="Enter"&&!busy) { e.preventDefault(); handleSubmit(); } };

  // Style helpers — defined inside component to avoid TDZ in production bundles
  const inpSt = (focused, err) => ({
    width:"100%", background:focused ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
    border:`1px solid ${err ? T.errorColor : focused ? "rgba(99,102,241,0.55)" : "rgba(255,255,255,0.08)"}`,
    color:T.text, borderRadius:12, padding:"12px 16px",
    fontSize:14, fontFamily:FONT, outline:"none", boxSizing:"border-box",
    transition:"all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
    ...(focused && !err ? { boxShadow:"0 0 0 3px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.03)" } : {}),
    ...(err            ? { boxShadow:"0 0 0 3px rgba(239,68,68,0.12)" }                    : {}),
  });
  const LBL  = { color:T.muted, fontSize:11.5, fontWeight:600, letterSpacing:"0.2px", marginBottom:7, display:"block" };
  const FE   = { color:T.errorColor, fontSize:11.5, marginTop:5 };
  const LBTN = { background:"none", border:"none", color:T.purple, cursor:"pointer", fontSize:12.5, fontFamily:FONT, fontWeight:600, padding:0 };
  const str   = React.useMemo(()=>calcStr(pw),[pw]);
  const strengthGradient = React.useMemo(() => {
    if (!pw) return "transparent";
    if (str.label === "Very weak" || str.label === "Weak") return "linear-gradient(90deg, #ef4444, #f97316)";
    if (str.label === "Okay") return "linear-gradient(90deg, #f97316, #eab308)";
    return "linear-gradient(90deg, #eab308, #10b981)";
  }, [pw, str.label]);

  const inp = (field, xtra={}) => ({
    className:"auth-input",
    style:{ ...inpSt(focus===field, !!fe[field]), ...xtra },
    onFocus:()=>setFocus(field),
    onBlur:()=>{ setFocus(""); mk(field); },
    onKeyDown:onKey,
    disabled:busy,
  });

  const socSt = (bg,color,bdr) => ({
    width:"100%", padding:"12px 14px",
    display:"flex", alignItems:"center", justifyContent:"center", gap:10,
    background:bg, border:bdr||"none", borderRadius:12,
    cursor:busy?"not-allowed":"pointer",
    fontSize:13.5, fontWeight:600, color, fontFamily:FONT,
    opacity:busy?0.6:1,
    transition:"transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s, background 0.2s",
  });

  const isL=mode===MO.LOGIN, isR=mode===MO.REGISTER, isRe=mode===MO.RESET;
  const showSoc = !isRe;

  const copy={
    [MO.LOGIN]:    { h:"Welcome back",        s:"Sign in to keep crafting your story." },
    [MO.REGISTER]: { h:"Create your account", s:"Start with 50 free AI credits — no card required." },
    [MO.RESET]:    { h:"Reset your password", s:"Enter your email and we'll send a reset link." },
  }[mode];

  return (
    <div className="auth-page" style={{minHeight:"100vh",background:T.bg,display:"flex",fontFamily:FONT,color:T.muted}}>
      <style>{GCSS}</style>

      {/* OAuth cancel / permission-denied toast — contextual, 1-click retry */}
      {toast&&(
        <div className="auth-toast" role="alert" style={{position:"fixed",top:20,right:20,zIndex:1000,maxWidth:340,
          background:"#111827",border:"1px solid rgba(255,255,255,0.12)",borderRadius:12,padding:"14px 16px",
          boxShadow:"0 16px 40px rgba(0,0,0,0.45)",color:T.text,fontFamily:FONT}}>
          <div style={{display:"flex",gap:9,alignItems:"flex-start"}}>
            <span style={{flexShrink:0,marginTop:1}}><AlertTriangle size={14} color="#f59e0b" strokeWidth={2}/></span>
            <span style={{flex:1,fontSize:12.5,lineHeight:1.45}}>{toast.message}</span>
            <button type="button" onClick={()=>setToast(null)} aria-label="Dismiss"
              style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:16,padding:0,lineHeight:1,flexShrink:0}}>×</button>
          </div>
          {toast.retry&&(
            <button type="button" className="auth-toast-retry"
              onClick={()=>{ const r=toast.retry; setToast(null); r(); }}
              style={{marginTop:10,marginLeft:23,background:"rgba(99,102,241,0.15)",border:"1px solid rgba(99,102,241,0.35)",
                color:"#a5b4fc",borderRadius:7,padding:"5px 12px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:FONT,transition:"background 0.15s"}}>
              Try again →
            </button>
          )}
        </div>
      )}

      {/* LEFT: Brand panel */}
      <aside className="auth-brand" style={{flex:"1 1 0",minWidth:0,position:"relative",overflow:"hidden",
        padding:"64px 56px",display:"flex",flexDirection:"column",justifyContent:"space-between",
        background:"linear-gradient(135deg,#0a0f1e 0%,#0e1428 50%,#0a0f1e 100%)",
        borderRight:`1px solid ${T.border}`}}>
        <div style={{position:"absolute",inset:0,zIndex:0,pointerEvents:"none",
          backgroundImage:"linear-gradient(rgba(99,102,241,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.04) 1px,transparent 1px)",
          backgroundSize:"48px 48px",maskImage:"radial-gradient(ellipse 90% 70% at 30% 40%,black 30%,transparent 90%)",
          WebkitMaskImage:"radial-gradient(ellipse 90% 70% at 30% 40%,black 30%,transparent 90%)"}}/>
        <div style={{position:"absolute",top:"20%",left:"-10%",width:480,height:480,borderRadius:"50%",zIndex:0,pointerEvents:"none",
          background:"radial-gradient(circle,rgba(99,102,241,0.18) 0%,transparent 70%)"}}/>

        <div style={{position:"relative",zIndex:1,maxWidth:480}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:6}}>
            <div style={{width:40,height:40,background:"linear-gradient(135deg,#4f46e5,#6366f1)",borderRadius:10,flexShrink:0,
              display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 24px rgba(79,70,229,0.32)"}}>
              <svg width="22" height="22" viewBox="0 0 100 100" role="img" aria-label="Cursivo" style={{filter:"drop-shadow(0 0 8px rgba(255,255,255,0.4))"}}>
                <path fill="#fff" fillRule="evenodd" d="M82.77 27.06 A 40 40 0 1 0 82.77 72.94 L 68.02 67.22 A 22 30 0 1 1 68.02 32.78 Z"/>
              </svg>
            </div>
            <span style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:28,fontWeight:700,color:T.text,letterSpacing:"-0.5px",fontStyle:"italic"}}>Cursivo</span>
          </div>
          <h1 style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:38,fontWeight:700,lineHeight:1.12,color:T.text,letterSpacing:"-0.8px",marginTop:88,marginBottom:18}}>
            ATS-ready resumes,<br/>
            <span style={{color:T.purple,fontStyle:"italic"}}>written in your voice.</span>
          </h1>
          <p style={{fontSize:15,lineHeight:1.6,color:"#94a3b8",marginBottom:44,maxWidth:420}}>
            Built for MBA cohorts, engineering campuses, and serious professionals who can't afford a mediocre first impression.
          </p>
          {[
            {svg:<path d="M7.5 1.5 L1.5 11h6L5 22.5l8-12.5h-5.5z" fill="#a5b4fc" stroke="none"/>,t:"AI bullet writing",d:"Turn vague duties into quantified achievements. Voice Guard keeps it sounding like you."},
            {svg:<><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></>,t:"25+ premium templates",d:"IIM Original, Tech, Finance, Law, Creative — all ATS-tested."},
            {svg:<><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></>,t:"50 free credits to start",d:"Enough to build one complete resume end-to-end. No card required."},
          ].map(({svg,t,d},i)=>(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:13,marginBottom:i<2?20:0}}>
              <div style={{width:28,height:28,borderRadius:8,flexShrink:0,background:"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.25)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{svg}</svg>
              </div>
              <div>
                <div style={{fontSize:13.5,fontWeight:600,color:"#e2e8f0",marginBottom:2}}>{t}</div>
                <div style={{fontSize:12,color:"#64748b",lineHeight:1.5}}>{d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:10,fontSize:12,color:"#475569"}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 8px #22c55e88",display:"inline-block"}}/>
          Cursivo is in public beta · India 🇮🇳
        </div>
      </aside>

      {/* RIGHT: Form panel */}
      <section className="auth-panel" style={{flex:"0 0 auto",width:"100%",maxWidth:480,
        padding:"48px 56px",display:"flex",flexDirection:"column",justifyContent:"center",background:T.bg}}>
        <div style={{width:"100%",maxWidth:380,margin:"0 auto"}}>

          {/* Mobile logo */}
          <div className="auth-mhdr" style={{display:"none",alignItems:"center",gap:10,marginBottom:28}}>
            <div style={{width:32,height:32,background:"linear-gradient(135deg,#4f46e5,#6366f1)",borderRadius:8,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="18" height="18" viewBox="0 0 100 100" role="img" aria-label="Cursivo">
                <path fill="#fff" fillRule="evenodd" d="M82.77 27.06 A 40 40 0 1 0 82.77 72.94 L 68.02 67.22 A 22 30 0 1 1 68.02 32.78 Z"/>
              </svg>
            </div>
            <span style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:22,fontWeight:700,color:T.text,fontStyle:"italic"}}>Cursivo</span>
          </div>

          {/* Mobile value propositions accordion */}
          <div className="auth-mprops" style={{marginBottom:20}}>
            <button type="button" onClick={()=>setShowMobileProps(v=>!v)}
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
                background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:8,
                padding:"8px 12px",color:"#a5b4fc",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:FONT}}>
              <span>Why choose Cursivo?</span>
              <span style={{transition:"transform 0.2s",transform:showMobileProps?"rotate(180deg)":"rotate(0deg)",display:"inline-block"}}>▼</span>
            </button>
            {showMobileProps && (
              <div className="auth-fe" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderTop:"none",borderRadius:"0 0 8px 8px",padding:12,fontSize:12,color:"#94a3b8",display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <strong style={{color:"#f1f5f9"}}>✓ AI Bullet Writing:</strong> Description to bullet in 1 click.
                </div>
                <div>
                  <strong style={{color:"#f1f5f9"}}>✓ ATS-Engine Checked:</strong> Tested across Workday, Greenhouse, Lever, etc.
                </div>
                <div>
                  <strong style={{color:"#f1f5f9"}}>✓ 26 India Templates:</strong> Tailored for IIMs, consulting, tech, law.
                </div>
                <div>
                  <strong style={{color:"#f1f5f9"}}>✓ 50 Free Credits:</strong> Build your first resume free.
                </div>
              </div>
            )}
          </div>

          <h2 className="auth-h2" style={{fontSize:26,fontWeight:700,color:T.text,marginBottom:6,letterSpacing:"-0.4px",fontFamily:'"Playfair Display",Georgia,serif'}}>{copy.h}</h2>
          <p style={{color:T.dim,fontSize:13.5,marginBottom:28,lineHeight:1.5}}>{copy.s}</p>

          {/* Tabs */}
          {!isRe&&(
            <div style={{display:"inline-flex",background:"rgba(255,255,255,0.04)",borderRadius:99,padding:3,marginBottom:28,border:`1px solid ${T.border}`}} role="tablist" aria-label="Sign in or create account">
              {[[MO.LOGIN,"Sign in"],[MO.REGISTER,"Create account"]].map(([m,lbl])=>(
                <button key={m} type="button" role="tab" aria-selected={mode===m} className="auth-tab-btn"
                  onClick={()=>sw(m)}
                  style={{padding:"7px 18px",background:mode===m?"#1d4ed8":"transparent",color:mode===m?"#fff":"#94a3b8",border:"none",borderRadius:99,cursor:"pointer",fontSize:12.5,fontFamily:FONT,fontWeight:mode===m?600:500,transition:"all 0.18s",letterSpacing:"0.1px"}}>
                  {lbl}
                </button>
              ))}
            </div>
          )}

          {/* Form — key triggers fade-slide animation on mode change */}
          <div key={mKey} className="auth-mode">
          <form ref={formRef} onSubmit={e=>{e.preventDefault();if(!busy)handleSubmit();}} noValidate>
            {/* Social buttons — shown first (happy path) */}
            {showSoc&&(
              <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
                {appSettings.googleLoginEnabled!==false&&(
                  <button type="button" onClick={handleGoogle} className="auth-google" disabled={busy} style={socSt("#fff","#0f172a","1px solid #e2e8f0")}>
                    {googleLoading ? (
                      <span className="auth-fe" style={{animation:"spin 1s linear infinite",color:"#0f172a",display:"inline-flex",marginRight:8}}>
                        <LoaderIcon size={16} color="#0f172a" strokeWidth={2} />
                      </span>
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true" style={{marginRight:8}}>
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                      </svg>
                    )}
                    {googleLoading ? "Signing you in…" : "Continue with Google"}
                  </button>
                )}
                {appSettings.linkedinLoginEnabled!==false&&(
                  <button type="button" onClick={handleLI} className="auth-linkedin" disabled={busy} aria-label="Continue with LinkedIn" style={socSt("#0A66C2","#fff")}>
                    {linkedinLoading ? (
                      <span className="auth-fe" style={{animation:"spin 1s linear infinite",color:"#fff",display:"inline-flex",marginRight:8}}>
                        <LoaderIcon size={16} color="#fff" strokeWidth={2} />
                      </span>
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" style={{marginRight:8}}>
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0h.003z"/>
                      </svg>
                    )}
                    {linkedinLoading ? "Redirecting…" : "Continue with LinkedIn"}
                  </button>
                )}
              </div>
            )}

            {/* "Or continue with email" divider — shown for both login and register */}
            {!isRe&&(
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                <div style={{flex:1,height:1,background:T.border}}/>
                <span style={{color:T.dim,fontWeight:600,fontSize:11,textTransform:"uppercase",letterSpacing:"0.6px"}}>{isR?"or sign up with email":"or continue with email"}</span>
                <div style={{flex:1,height:1,background:T.border}}/>
              </div>
            )}

            {/* Email form body — always visible, equal weight to social sign-in */}
            <div className={isL?"auth-email-in":""}>

                {/* Name */}
                {isR&&(
                  <div style={{marginBottom:16}}>
                    <label htmlFor="rn" style={LBL}>Full name</label>
                    <input id="rn" ref={f1Ref} aria-label="Full name" value={name}
                      onChange={e=>{setName(e.target.value);clr();}} placeholder="e.g. Priya Sharma" autoComplete="name" {...inp("name")}/>
                    {fe.name&&<div className="auth-fe" style={FE}>{fe.name}</div>}
                  </div>
                )}

                {/* Email */}
                <div style={{marginBottom:16}}>
                  <label htmlFor="ae" style={LBL}>Email address</label>
                  <input id="ae" ref={!isR?f1Ref:undefined} type="email" aria-label="Email address"
                    value={email} onChange={e=>{setEmail(e.target.value);clr();}} placeholder="you@example.com" autoComplete="email" {...inp("email")}/>
                  {fe.email&&<div className="auth-fe" style={FE}>{fe.email}</div>}
                </div>

                {/* Password */}
                {!isRe&&(
                  <div style={{marginBottom:isR?16:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                      <label htmlFor="ap" style={{...LBL,marginBottom:0}}>Password</label>
                      {isL&&(
                        <button type="button" className="auth-link" onClick={()=>sw(MO.RESET)}
                          style={{...LBTN,fontSize:11.5}}>Forgot password?</button>
                      )}
                    </div>
                    <div style={{position:"relative"}}>
                      <input id="ap" type={showPw?"text":"password"} aria-label="Password"
                        value={pw} onChange={e=>{setPw(e.target.value);clr();}} placeholder="••••••••"
                        autoComplete={isL?"current-password":"new-password"} {...inp("pw",{paddingRight:42})}/>
                      <button type="button" onClick={()=>setShowPw(v=>!v)} className="auth-eye"
                        aria-label={showPw?"Hide password":"Show password"}
                        style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:15,padding:4,display:"flex",alignItems:"center",borderRadius:4}}>
                        {showPw?<EyeOffIcon size={15} color="#475569"/>:<EyeIcon size={15} color="#475569"/>}
                      </button>
                    </div>
                    {isR&&(
                      <div style={{marginTop:8}}>
                        <div style={{height:4,background:"#1e293b",borderRadius:4,overflow:"hidden",marginBottom:8}}>
                           <div style={{height:"100%",width:pw ? `${str.pct}%` : "0%",background:strengthGradient,transition:"width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.25s",borderRadius:4}}/>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:5,fontSize:11,color:T.dim}}>
                          {pw && (
                            <div style={{color:str.color,fontWeight:600,fontSize:10.5,marginBottom:2}}>
                              Strength: {str.label}
                            </div>
                          )}
                          <div style={{display:"flex",alignItems:"center",gap:6,color:pw.length >= 8 ? "#22c55e" : T.dim,transition:"color 0.2s"}}>
                            <span style={{display:"inline-flex",width:6,height:6,borderRadius:"50%",background:pw.length >= 8 ? "#22c55e" : "#475569"}}/>
                            At least 8 characters
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:6,color:(/[a-z]/.test(pw) && /[A-Z]/.test(pw)) ? "#22c55e" : T.dim,transition:"color 0.2s"}}>
                            <span style={{display:"inline-flex",width:6,height:6,borderRadius:"50%",background:(/[a-z]/.test(pw) && /[A-Z]/.test(pw)) ? "#22c55e" : "#475569"}}/>
                            Mixed case letters (A-Z, a-z)
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:6,color:(/\d/.test(pw) || /[^A-Za-z0-9]/.test(pw)) ? "#22c55e" : T.dim,transition:"color 0.2s"}}>
                            <span style={{display:"inline-flex",width:6,height:6,borderRadius:"50%",background:(/\d/.test(pw) || /[^A-Za-z0-9]/.test(pw)) ? "#22c55e" : "#475569"}}/>
                            At least one number or symbol
                          </div>
                        </div>
                      </div>
                    )}
                    {fe.pw&&<div className="auth-fe" style={FE}>{fe.pw}</div>}
                  </div>
                )}

                {/* Confirm password */}
                {isR&&(
                  <div style={{marginBottom:20}}>
                    <label htmlFor="ac" style={LBL}>Confirm password</label>
                    <div style={{position:"relative"}}>
                      <input id="ac" type={showC?"text":"password"} aria-label="Confirm password"
                        value={cpw} onChange={e=>{setCpw(e.target.value);clr();}} placeholder="••••••••"
                        autoComplete="new-password" {...inp("cpw",{paddingRight:60})}/>
                      <div style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",display:"flex",alignItems:"center",gap:6}}>
                        {cpw && (
                          <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
                            {pw === cpw ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            )}
                          </span>
                        )}
                        <button type="button" onClick={()=>setShowC(v=>!v)} className="auth-eye"
                          aria-label={showC?"Hide password":"Show password"}
                          style={{background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:15,padding:4,display:"flex",alignItems:"center",borderRadius:4}}>
                          {showC?<EyeOffIcon size={15} color="#475569"/>:<EyeIcon size={15} color="#475569"/>}
                        </button>
                      </div>
                    </div>
                    {fe.cpw&&<div className="auth-fe" style={FE}>{fe.cpw}</div>}
                  </div>
                )}

                {/* Error banner */}
                {err&&(
                  <div role="alert" style={{background:T.errorBg,border:`1px solid ${T.errorBorder}`,color:"#fca5a5",borderRadius:8,padding:"10px 13px",fontSize:12.5,marginBottom:16,display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <AlertTriangle size={13} color="#f87171" strokeWidth={2}/>{err}
                    </div>
                    {isEmailConflict(eCode)&&appSettings.googleLoginEnabled!==false&&(
                      <button type="button" onClick={handleGoogle} className="auth-cfta"
                        style={{alignSelf:"flex-start",background:"rgba(255,255,255,0.06)",border:`1px solid ${T.border}`,color:"#e2e8f0",borderRadius:6,padding:"5px 10px",fontSize:11.5,fontFamily:FONT,cursor:"pointer",fontWeight:600,transition:"background 0.15s"}}>
                        Try signing in with Google →
                      </button>
                    )}
                  </div>
                )}
                {ok&&(
                  <div role="status" style={{background:T.successBg,border:`1px solid ${T.successBorder}`,color:"#86efac",borderRadius:8,padding:"10px 13px",fontSize:12.5,marginBottom:16,display:"flex",alignItems:"center",gap:7}}>
                    <CheckIcon size={13} color="#4ade80" strokeWidth={2.5}/>{ok}
                  </div>
                )}

                {/* Inline legal consent — register only, no separate checkbox click required */}
                {isR&&(
                  <p style={{fontSize:11,color:T.dim,lineHeight:1.5,marginBottom:14,textAlign:"center"}}>
                    By clicking "Create account", you agree to Cursivo's{" "}
                    <a href="/terms.html" target="_blank" rel="noopener" style={{color:T.purple,textDecoration:"underline"}}>Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy.html" target="_blank" rel="noopener" style={{color:T.purple,textDecoration:"underline"}}>Privacy Policy</a>.
                  </p>
                )}

                {/* Submit */}
                <button type="submit" className="auth-submit"
                  disabled={busy}
                  aria-busy={busy}
                  style={{
                    width:"100%",padding:"13px",
                    background:busy?"rgba(99,102,241,0.25)":"linear-gradient(135deg,#4f46e5,#6366f1)",
                    color:busy?"#94a3b8":"#fff",
                    border:"none",borderRadius:9,
                    cursor:busy?"not-allowed":"pointer",
                    fontSize:13.5,fontWeight:600,fontFamily:FONT,
                    transition:"transform 0.1s,box-shadow 0.15s,background 0.2s",
                    boxShadow:busy?"none":"0 6px 24px rgba(79,70,229,0.32)",
                    letterSpacing:"0.1px",
                  }}>
                  {busy
                    ?(isL?"Signing you in…":isR?"Creating your account…":"Sending reset link…")
                    :isL?"Sign in →":isR?"Create account →":"Send reset link →"}
                </button>

                {/* Reset: back link */}
                {isRe&&(
                  <div style={{textAlign:"center",marginTop:16}}>
                    <button type="button" className="auth-link" onClick={()=>sw(MO.LOGIN)} style={LBTN}>
                      ← Back to sign in
                    </button>
                  </div>
                )}
              </div>

          </form>
          </div>

          {/* Footer */}
          {!isRe&&(
            <div style={{textAlign:"center",marginTop:28,color:T.dim,fontSize:12.5,paddingTop:24,borderTop:`1px solid ${T.border}`}}>
              {isL
                ?<span>No account?{" "}<button type="button" className="auth-link" onClick={()=>sw(MO.REGISTER)} style={LBTN}>Create one free</button></span>
                :<span>Already have an account?{" "}<button type="button" className="auth-link" onClick={()=>sw(MO.LOGIN)} style={LBTN}>Sign in</button></span>}
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
